/**
 * Scanner leads + autopilot API client.
 *
 * The leads table is the scanner's output as a work queue: what the autopilot
 * found, who has been called, and what the robot is scheduled to do next.
 * Mirrors apps/api/src/modules/ariregister/scan-leads.routes.ts and
 * scan-autopilot.routes.ts.
 */
import { api } from "./api";
import type { BrainBadge } from "./siteBrain";
import { toQuery, type SiteKind } from "./siteScan";

export type LeadStatus = "new" | "contacted" | "offer" | "won" | "ignored";
export type LeadSeverity = "hot" | "warm" | "cool";

export const LEAD_STATUSES: LeadStatus[] = ["new", "contacted", "offer", "won", "ignored"];
export const LEAD_SEVERITIES: LeadSeverity[] = ["hot", "warm", "cool"];

/** Why the autopilot flagged this site. `id` is the i18n key suffix. */
export interface LeadReason {
  id: string;
  weight: number;
  vars?: Record<string, string | number>;
}

export interface ScanLead {
  domain: string;
  url: string;
  companyName: string | null;
  regCode: string | null;
  email: string | null;
  phone: string | null;
  cms: string | null;
  cmsName: string | null;
  cmsVersion: string | null;
  cmsLatest: string | null;
  versionGap: string | null;
  outdatedPlugins: number;
  abandonedPlugins: number;
  /** Already weighted by criticality: the priority, not the defect count. */
  score: number;
  severity: LeadSeverity;
  /** What the site is for. Null on leads qualified before profiling existed. */
  siteKind: SiteKind | null;
  /** 0-100, how much of the business runs through it. Null = not known. */
  criticality: number | null;
  reasons: LeadReason[] | null;
  status: LeadStatus;
  note: string | null;
  assignedTo: string | null;
  crmLeadId: string | null;
  firstSeenAt: string;
  /** When the autopilot last confirmed this lead. The "exact time" column. */
  checkedAt: string;
  contactedAt: string | null;
  /** Development-brain state for this domain, joined in by the list route.
   *  Null when the site has never been run - the ordinary case. */
  brain: BrainBadge | null;
}

export interface LeadListParams {
  q?: string;
  status?: LeadStatus[];
  severity?: LeadSeverity[];
  /** SiteKind. `unknown` also covers leads qualified before profiling existed. */
  kind?: SiteKind[];
  cms?: string[];
  hasEmail?: boolean;
  hasCompany?: boolean;
  scoreMin?: number;
  criticalityMin?: number;
  sort?: "checked" | "score" | "company" | "severity" | "first" | "status";
  order?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface LeadListResult {
  items: ScanLead[];
  total: number;
  page: number;
  pageSize: number;
  counts: {
    status: Record<string, number>;
    severity: Record<string, number>;
    /** Leads per SiteKind. Nulls are folded into `unknown`. */
    kind: Record<string, number>;
  };
}

export interface AutopilotSettings {
  enabled: boolean;
  intervalMinutes: number;
  batchSize: number;
  rescanAfterDays: number;
  minScore: number;
  requireContact: boolean;
  discoverFromRegister: boolean;
  dailyCap: number;
}

export interface AutopilotRun {
  id: string;
  trigger: "schedule" | "manual";
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  checked: number;
  rescanned: number;
  added: number;
  updated: number;
  dropped: number;
  failed: number;
  discovered: number;
  error: string | null;
  /**
   * Why a run did nothing: `daily-cap` | `discovery-off` | `register-done` |
   * `no-candidates`. Null when the run had work to do. The id is the i18n key
   * suffix (`leads.autopilot.note.<id>`).
   */
  note: RunNote | null;
}

export type RunNote = "daily-cap" | "discovery-off" | "register-done" | "no-candidates";

/** `daily-cap` -> `dailyCap`, so a server enum can address a message key. */
export const noteKey = (note: RunNote): string =>
  note.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export interface AutopilotState {
  settings: AutopilotSettings;
  busy: boolean;
  runs: AutopilotRun[];
  nextRunAt: string | null;
  stats: { scans: number; due: number; leads: number; newLeads: number };
}

export const listLeads = (params: LeadListParams = {}): Promise<LeadListResult> =>
  api.get<LeadListResult>(`/admin/ariregister/scan-leads${toQuery(params as Record<string, unknown>)}`);

export const updateLead = (
  domain: string,
  patch: { status?: LeadStatus; note?: string | null; assignedTo?: string | null },
): Promise<ScanLead> =>
  api.patch<ScanLead>(`/admin/ariregister/scan-leads/${encodeURIComponent(domain)}`, patch);

/** Push a scanner lead into the CRM pipeline. Idempotent on the server. */
export const pushLeadToCrm = (domain: string): Promise<{ ok: boolean; leadId: string; created: boolean }> =>
  api.post<{ ok: boolean; leadId: string; created: boolean }>(
    `/admin/ariregister/scan-leads/${encodeURIComponent(domain)}/crm`,
    {},
  );

export const dismissLead = (domain: string): Promise<{ ok: boolean }> =>
  api.del<{ ok: boolean }>(`/admin/ariregister/scan-leads/${encodeURIComponent(domain)}`);

export const leadsCsvUrl = (params: LeadListParams = {}): string =>
  `/api/admin/ariregister/scan-leads/export.csv${toQuery(params as Record<string, unknown>)}`;

export const getAutopilot = (): Promise<AutopilotState> =>
  api.get<AutopilotState>("/admin/ariregister/scan-autopilot");

export const saveAutopilot = (
  patch: Partial<AutopilotSettings>,
): Promise<{ ok: boolean; settings: AutopilotSettings }> =>
  api.put<{ ok: boolean; settings: AutopilotSettings }>("/admin/ariregister/scan-autopilot", patch);

export const runAutopilot = (limit?: number): Promise<{ ok: boolean; started: boolean }> =>
  api.post<{ ok: boolean; started: boolean }>("/admin/ariregister/scan-autopilot/run", limit ? { limit } : {});
